import React from "react";
import { Check, MessageSquare, MessageCircle, Smartphone, Facebook, Instagram, Globe, Database, Briefcase, Layers } from "lucide-react";
import { Separator } from "@/components/ui/separator";
const solutionFeatures = ["Centralice todas las conversaciones en una sola plataforma.", "Automatice respuestas y asignaciones con inteligencia artificial.", "Obtenga reportes y métricas clave en tiempo real."];
const responseFeatures = ["Historial completo de sus chats.", "Agentes que se apoyan y atienden diferentes grupos", "Grupos de atención con jerarquía y automatización.", "Bot de información general en árbol de decisión o integrado con IA."];
const integratedChannels = [{
  name: "WhatsApp Business",
  icon: MessageCircle
}, {
  name: "SMS",
  icon: Smartphone
}, {
  name: "Web Chat",
  icon: Globe
}, {
  name: "Facebook Messenger",
  icon: Facebook
}, {
  name: "Instagram",
  icon: Instagram
}];
const crmIntegrations = [{
  name: "Clientify",
  icon: Database
}, {
  name: "Salesforce",
  icon: Briefcase
}, {
  name: "Bitrix",
  icon: Layers
}];
export default function OmnichannelCapabilities() {
  return <section id="omnichannelcapabilities" className="bg-background py-10 md:py-14">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        
        {/* Section Header */}
        <div className="text-center max-w-5xl mx-auto mb-8 md:mb-10">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-primary font-default mb-6">
            ¿Que es el Chat Omnicanal?
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            El Chat Omnicanal con WhatsApp es una solución innovadora que permite a las empresas centralizar y gestionar todas las conversaciones con sus clientes desde una sola plataforma. Integrando WhatsApp Business API junto con otros canales como Facebook Messenger, Instagram, y webchat, esta herramienta optimiza la comunicación en tiempo real, mejorando la experiencia del usuario y la eficiencia del equipo de atención. Con funciones avanzadas como automatización de respuestas, asignación de chats a agentes, integración con CRM y reportes detallados, el Chat Omnicanal garantiza una atención rápida, organizada y escalable, ayudando a las empresas a fidelizar clientes y aumentar sus conversiones.
          </p>
        </div>

        {/* Capabilities Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Left Column: Solutions & Response */}
          <div className="flex flex-col gap-10">
            
            {/* Solutions Feature */}
            <div>
              <h3 className="text-2xl md:text-3xl font-semibold text-primary font-default mb-6">
                Solución con Chat Omnicanal:
              </h3>
              <ul className="flex flex-col gap-4">
                {solutionFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <div className="bg-chart-3 text-white rounded-sm flex items-center justify-center shrink-0 mt-1 size-5">
                      <Check className="size-3.5 stroke-[3]" />
                    </div>
                    <span className="text-muted-foreground italic text-base md:text-lg">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>

            <Separator className="opacity-50" />

            {/* Response Feature */}
            <div>
              <h3 className="text-2xl md:text-3xl font-semibold text-primary font-default mb-6 leading-tight">
                Con nuestra solución de Chat Omnicanal podrá conocer los tiempos de respuesta y las respuestas a sus clientes
              </h3>
              <ul className="flex flex-col gap-4">
                {responseFeatures.map((feature, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <div className="bg-chart-3 text-white rounded-sm flex items-center justify-center shrink-0 mt-1 size-5">
                      <Check className="size-3.5 stroke-[3]" />
                    </div>
                    <span className="text-muted-foreground italic text-base md:text-lg">
                      {feature}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Integrated Channels Box */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-md">
              <h4 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                <MessageSquare className="size-5" />
                Canales integrados
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {integratedChannels.map((channel, i) => <div key={i} data-index={i} className="flex items-center gap-3 p-2 rounded-md hover:bg-accent transition-colors">
                    <div className="bg-primary/10 text-primary p-2 rounded-lg">
                      <channel.icon className="size-5" />
                    </div>
                    <span className="font-medium text-foreground">{channel.name}</span>
                  </div>)}
              </div>
            </div>

            {/* CRM Integrations Box */}
            <div className="bg-card p-6 rounded-lg border border-border shadow-md">
              <h4 className="text-xl font-bold text-primary mb-6 flex items-center gap-2">
                <Layers className="size-5" />
                Integración con CRM
              </h4>
              <div className="flex flex-wrap gap-6">
                {crmIntegrations.map((crm, i) => <div key={i} data-index={i} className="flex items-center gap-3">
                    <div className="bg-secondary/10 text-secondary p-2 rounded-lg">
                      <crm.icon className="size-5" />
                    </div>
                    <span className="font-medium text-foreground">{crm.name}</span>
                  </div>)}
              </div>
            </div>

          </div>

          {/* Right Column: Visual Diagram & Exclusive Services */}
          <div className="flex flex-col gap-8">
            <div className="relative rounded-lg overflow-hidden shadow-lg border border-border/50 bg-white p-4">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/pbx_virtual_benefits_diagram.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/pbx_virtual_benefits_diagram.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1685354/img/pbx_virtual_benefits_diagram.png-wvc-sizes" alt="Beneficios de PBX Virtual y Omnicanalidad" className="w-full h-auto object-contain" loading="lazy" />
            </div>
            
            <div className="bg-accent/50 p-6 rounded-lg border border-primary/20">
              <h4 className="text-lg font-bold text-primary mb-3">Nuestros Servicios Exclusivos</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">• Firewall integrado en centrales</li>
                  <li className="flex items-center gap-2">• Enlace Asterisk y Firewall</li>
                  <li className="flex items-center gap-2">• Tarificador para Hoteles</li>
                  <li className="flex items-center gap-2">• Envío de SMS masivos</li>
                </ul>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">• Campañas de mercadeo grabadas</li>
                  <li className="flex items-center gap-2">• Desarrollos a la medida</li>
                  <li className="flex items-center gap-2">• Fabricantes e integradores</li>
                </ul>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}